import { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Button } from '@cbhq/cds-web/buttons';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { HeroSquare } from '@cbhq/cds-web/illustrations';
import { VStack } from '@cbhq/cds-web/layout/VStack';
import { ModalFooter } from '@cbhq/cds-web/overlays';
import { Modal } from '@cbhq/cds-web/overlays/modal/Modal';
import { ModalBody } from '@cbhq/cds-web/overlays/modal/ModalBody';
import { ModalHeader } from '@cbhq/cds-web/overlays/modal/ModalHeader';
import { TextBody, TextTitle1 } from '@cbhq/cds-web/typography';

import { useShowPasskeyGuideTourModel } from '../hooks/useShowPasskeyGuideTourModel';

export const messages = defineMessages({
  passkeyGuideTourModalTitle: {
    id: 'passkeyGuideTourModal.title',
    defaultMessage: 'Make signing in easy with passkeys',
    description: 'Title for the passkey guide tour modal',
  },
  passkeyGuideTourModalDescription: {
    id: 'passkeyGuideTourModal.description',
    defaultMessage:
      'A passkey is a safe and easy way to protect your account. No more passwords or text codes. Simply use your phone and confirm it’s you.',
    description: 'Description for the passkey guide tour modal',
  },
  passkeyGuideTourModalPrimaryButton: {
    id: 'passkeyGuideTourModal.primaryButton',
    defaultMessage: 'Yes, show me how!',
    description: 'Primary button for the passkey guide tour modal',
  },
  passkeyGuideTourModalSecondaryButton: {
    id: 'passkeyGuideTourModal.secondaryButton',
    defaultMessage: 'Learn more',
    description: 'Secondary button for the passkey guide tour modal',
  },
});

const LEARN_MORE_URL =
  'https://www.coinbase.com/blog/introducing-passkeys-a-safer-and-easier-way-to-sign-in-to-coinbase';

const PASSKEY_UPSELL_REDIRECT_URL = 'https://accounts.coinbase.com/security/settings?tour=passkey';

export function PasskeyGuideTourModal() {
  const { show, hideModel } = useShowPasskeyGuideTourModel();
  const { formatMessage } = useIntl();
  const { isPhone } = useBreakpoints();

  const [loading, setLoading] = useState(false);

  const onPressPrimaryButton = useCallback(() => {
    setLoading(true);
    window.location.href = PASSKEY_UPSELL_REDIRECT_URL;
  }, []);

  const onPressSecondaryButton = useCallback(() => {
    window.open(LEARN_MORE_URL, '_blank');
  }, []);

  return (
    <Modal visible={show} onRequestClose={hideModel} width={isPhone ? undefined : 430}>
      <ModalHeader />
      <ModalBody>
        <VStack alignItems="center" gap={3}>
          <HeroSquare dimension={isPhone ? '200x200' : '240x240'} name="privateKey" />
          <TextTitle1 as={isPhone ? 'h3' : 'h2'} textAlign="center">
            {formatMessage(messages.passkeyGuideTourModalTitle)}
          </TextTitle1>
          <TextBody as="p" textAlign="center" display="block">
            {formatMessage(messages.passkeyGuideTourModalDescription)}
          </TextBody>
        </VStack>
      </ModalBody>
      <ModalFooter
        primaryAction={
          <Button onClick={onPressPrimaryButton} variant="primary" loading={loading} compact>
            {formatMessage(messages.passkeyGuideTourModalPrimaryButton)}
          </Button>
        }
        secondaryAction={
          <Button onClick={onPressSecondaryButton} variant="secondary" compact>
            {formatMessage(messages.passkeyGuideTourModalSecondaryButton)}
          </Button>
        }
      />
    </Modal>
  );
}
